import React, { useState, useRef, useEffect } from "react";
import { useCurrency } from "./CurrencyContext";
import getSymbolFromCurrency from "currency-symbol-map";

// ✅ Local data — no API call, no CORS issue
const COUNTRIES = [
  { name: "United States", cca2: "US", currency: "USD", flag: "🇺🇸" },
  { name: "United Kingdom", cca2: "GB", currency: "GBP", flag: "🇬🇧" },
  { name: "European Union", cca2: "EU", currency: "EUR", flag: "🇪🇺" },
  { name: "Saudi Arabia", cca2: "SA", currency: "SAR", flag: "🇸🇦" },
  { name: "United Arab Emirates", cca2: "AE", currency: "AED", flag: "🇦🇪" },
  { name: "Pakistan", cca2: "PK", currency: "PKR", flag: "🇵🇰" },
  { name: "India", cca2: "IN", currency: "INR", flag: "🇮🇳" },
  { name: "Canada", cca2: "CA", currency: "CAD", flag: "🇨🇦" },
  { name: "Australia", cca2: "AU", currency: "AUD", flag: "🇦🇺" },
  { name: "Japan", cca2: "JP", currency: "JPY", flag: "🇯🇵" },
  { name: "China", cca2: "CN", currency: "CNY", flag: "🇨🇳" },
  { name: "Turkey", cca2: "TR", currency: "TRY", flag: "🇹🇷" },
  { name: "Kuwait", cca2: "KW", currency: "KWD", flag: "🇰🇼" },
  { name: "Qatar", cca2: "QA", currency: "QAR", flag: "🇶🇦" },
  { name: "Bahrain", cca2: "BH", currency: "BHD", flag: "🇧🇭" },
  { name: "Oman", cca2: "OM", currency: "OMR", flag: "🇴🇲" },
  { name: "Jordan", cca2: "JO", currency: "JOD", flag: "🇯🇴" },
  { name: "Egypt", cca2: "EG", currency: "EGP", flag: "🇪🇬" },
  { name: "Bangladesh", cca2: "BD", currency: "BDT", flag: "🇧🇩" },
  { name: "Malaysia", cca2: "MY", currency: "MYR", flag: "🇲🇾" },
  { name: "Singapore", cca2: "SG", currency: "SGD", flag: "🇸🇬" },
  { name: "Switzerland", cca2: "CH", currency: "CHF", flag: "🇨🇭" },
  { name: "Sweden", cca2: "SE", currency: "SEK", flag: "🇸🇪" },
  { name: "Norway", cca2: "NO", currency: "NOK", flag: "🇳🇴" },
  { name: "Denmark", cca2: "DK", currency: "DKK", flag: "🇩🇰" },
  { name: "Brazil", cca2: "BR", currency: "BRL", flag: "🇧🇷" },
  { name: "Mexico", cca2: "MX", currency: "MXN", flag: "🇲🇽" },
  { name: "South Africa", cca2: "ZA", currency: "ZAR", flag: "🇿🇦" },
  { name: "Nigeria", cca2: "NG", currency: "NGN", flag: "🇳🇬" },
  { name: "Indonesia", cca2: "ID", currency: "IDR", flag: "🇮🇩" },
];

const CountryCurrencyDropdown = () => {
  const { currency, setCurrency, setRate } = useCurrency();
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Selected country derive karo current currency se
  const selected = COUNTRIES.find((c) => c.currency === currency) || COUNTRIES[0];

  // Close on outside click
  useEffect(() => {
    const handleClick = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setOpen(false);
        setSearch("");
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  // Currency change hone par exchange rate fetch karo
  const handleSelect = async (country) => {
    setOpen(false);
    setSearch("");
    setCurrency(country.currency);

    try {
      // Free API — CORS issue nahi hoga
      const res = await fetch(
        `https://api.exchangerate-api.com/v4/latest/USD`
      );
      const data = await res.json();
      const rate = data.rates[country.currency] || 1;
      setRate(rate);
    } catch (err) {
      console.error("Rate fetch error:", err);
      setRate(1); // Fallback
    }
  };

  const filtered = COUNTRIES.filter(
    (c) =>
      c.name.toLowerCase().includes(search.toLowerCase()) ||
      c.currency.toLowerCase().includes(search.toLowerCase())
  );

  const currencySymbol =
    selected.currency === "SAR"
      ? "﷼"
      : getSymbolFromCurrency(selected.currency) || selected.currency;

  return (
    <div ref={dropdownRef} className="relative w-full">
      {/* Trigger Button */}
      <button
        onClick={() => setOpen((prev) => !prev)}
        className="flex items-center gap-2 w-full px-3 py-2 border border-gray-200 rounded-lg bg-white hover:border-orange-400 transition-colors text-sm"
      >
        <span className="text-lg leading-none">{selected.flag}</span>
        <span className="font-medium text-gray-700">{selected.currency}</span>
        <span className="text-gray-400 ml-auto">▾</span>
      </button>

      {/* Dropdown */}
      {open && (
        <div className="absolute top-full left-0 mt-1 w-56 bg-white border border-gray-200 rounded-xl shadow-xl z-[999] overflow-hidden">
          {/* Search */}
          <div className="p-2 border-b border-gray-100">
            <input
              type="text"
              placeholder="Search country or currency..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full px-3 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-orange-400"
              autoFocus
            />
          </div>

          {/* List */}
          <ul className="max-h-56 overflow-y-auto">
            {filtered.length > 0 ? (
              filtered.map((country) => (
                <li key={country.cca2}>
                  <button
                    onClick={() => handleSelect(country)}
                    className={`w-full flex items-center gap-3 px-3 py-2 text-sm hover:bg-orange-50 hover:text-orange-600 transition-colors ${
                      currency === country.currency
                        ? "bg-orange-50 text-orange-600 font-medium"
                        : "text-gray-700"
                    }`}
                  >
                    <span className="text-base">{country.flag}</span>
                    <span className="flex-1 text-left">{country.name}</span>
                    <span className="text-xs text-gray-400 font-mono">
                      {country.currency}
                    </span>
                  </button>
                </li>
              ))
            ) : (
              <li className="px-4 py-3 text-sm text-gray-400 text-center">
                No results found
              </li>
            )}
          </ul>
        </div>
      )}
    </div>
  );
};

export default CountryCurrencyDropdown;